/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {useMedia} from '../../hooks/useMedia';
// eslint-disable-next-line ion-eslint-rules/fast-refresh-verifier
import useThemeContext from '../../theme/useThemeContext';

/**
 * Defines the style props used by the docked drawer overrides.
 */
interface StyleProps {
  offset: number;
}

/**
 * Defines the `Drawer` props that keep a drawer docked beside the
 * current-theme side nav from covering it.
 */
export interface SideNavDockedDrawerProps {
  backdropClassName?: string;
  className?: string;
  disableEnforceFocus: boolean;
}

// a temporary Drawer's modal root and backdrop are fixed to the whole
// viewport by default, which lays both over the side nav - swallowing its
// clicks and greying it out. The root's inline `left: 0` needs !important
// to be overridden; its legacy negative margin is cleared for the same
// reason, so the nav's full width stays reachable ...
const useStyles = makeStyles(() => ({
  root : {
    left       : ({offset}: StyleProps) => `${offset}px !important`,
    marginLeft : '0px !important'
  },
  backdrop : {
    left : ({offset}: StyleProps) => `${offset}px !important`
  }
}));

const UNDOCKED_DRAWER_PROPS: SideNavDockedDrawerProps = {
  disableEnforceFocus : false
};

/**
 * Returns the `Drawer` props that keep a drawer opened beside the
 * current-theme desktop side nav from covering it, so every side nav item
 * stays visible and clickable while the drawer is open. The drawer's modal
 * root and backdrop start at the given offset, and focus is not trapped in
 * the drawer, so the nav stays reachable by keyboard too. Legacy theme and
 * non-desktop layouts receive no overrides.
 *
 * ```typescript
 *
 *   import {useSideNavDockedDrawer} from '@ion/dls/src/components/navigation/useSideNavDockedDrawer';
 *   ...
 *
 *   const docked = useSideNavDockedDrawer(dlsSidebarWidth(collapsed));
 *
 *   <Drawer open={open}
 *     className={docked.className}
 *     disableEnforceFocus={docked.disableEnforceFocus}
 *     BackdropProps={{className: docked.backdropClassName}}
 *     onClose={onClose} />
 *
 * ```
 *
 * @param   offset The side nav's current pixel width.
 * @returns The modal root class, backdrop class, and focus trap flag.
 */
export const useSideNavDockedDrawer = (offset: number): SideNavDockedDrawerProps => {
  const {isCurrent} = useThemeContext();
  const {isDesktop} = useMedia();
  const classes     = useStyles({offset});
  const docked      = isCurrent && isDesktop;

  return useMemo(() => (
    docked ? {
      backdropClassName   : classes.backdrop,
      className           : classes.root,
      disableEnforceFocus : true
    } : UNDOCKED_DRAWER_PROPS
  ), [classes.backdrop, classes.root, docked]);
};

export default useSideNavDockedDrawer;
